// OptimizedImage component for progressive image loading with placeholder and error handling
import { useState, useRef, useEffect } from 'react';

// Props for OptimizedImage
// src: image source URL
// alt: alt text for image
// className: custom CSS classes
// placeholder: color or gradient placeholder class
// blurDataUrl: optional blurred image placeholder
// priority: if true, image is preloaded and loaded eagerly
// onLoad: callback when image loads
// onError: callback when image fails to load
interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  blurDataUrl?: string;
  priority?: boolean;
  onLoad?: () => void;
  onError?: () => void;
}

// OptimizedImage handles progressive loading, placeholder, and error UI
export const OptimizedImage = ({
  src,
  alt,
  className = '',
  placeholder,
  blurDataUrl,
  priority = false,
  onLoad,
  onError,
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Preload image if priority is set
  useEffect(() => {
    if (priority && imgRef.current) {
      // Preload the image for critical images
      const img = new Image();
      img.src = src;
      img.onload = () => setIsLoaded(true);
      img.onerror = () => setHasError(true);
    }
  }, [src, priority]);

  // Handler for image load event
  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  // Handler for image error event
  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Blurred placeholder shown while loading if blurDataUrl is provided */}
      {!isLoaded && blurDataUrl && (
        <img
          src={blurDataUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover filter blur-sm scale-110 transition-opacity duration-300"
          aria-hidden="true"
        />
      )}

      {/* Color or gradient placeholder shown while loading if no blurDataUrl */}
      {!isLoaded && !blurDataUrl && placeholder && (
        <div
          className={`absolute inset-0 ${placeholder} animate-pulse`}
          aria-hidden="true"
        />
      )}

      {/* Main image element with fade-in transition */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        fetchPriority={priority ? 'high' : 'auto'}
        onLoad={handleLoad}
        onError={handleError}
      />

      {/* Error state UI if image fails to load */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200 text-gray-500">
          <span className="text-sm">Failed to load image</span>
        </div>
      )}
    </div>
  );
};
